import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { GripVertical, ArrowUp, ArrowDown, MapPin, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BuildingScheduler
 * -----------------
 * Crew Leader tool to order the buildings of a property and assign a
 * specific Site Agent to each. Uses HTML5 drag-drop with click-arrow
 * fallback for accessibility. Persists to PUT /api/ops/building-schedule.
 */
export function BuildingScheduler({ user }) {
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [pickPropertyId, setPickPropertyId] = useState('');
  const [rows, setRows] = useState([]); // [{building_number, order, assigned_agent_id}]
  const [saving, setSaving] = useState(false);
  const [structure, setStructure] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [batchAgent, setBatchAgent] = useState('');
  const [selected, setSelected] = useState({}); // building_number → bool

  // Fetch properties the CL is scoped to + downstream agents.
  useEffect(() => {
    (async () => {
      try {
        const [propRes, teamRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/company/my-daily-assignments`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' }),
        ]);
        const propData = propRes.ok ? await propRes.json() : {};
        const teamData = teamRes.ok ? await teamRes.json() : { members: [] };
        setProperties(propData.properties || []);
        const my = (teamData.members || []).filter(
          (m) => (m.user_role === 'agent' || m.user_role === 'site_agent') &&
                 (m.managed_by === user.user_id || m.assigned_by === user.user_id),
        );
        setAgents(my);
      } catch {
        // Silent — the CL just sees an empty state.
      }
    })();
  }, [user.user_id]);

  // Load property structure + existing schedule when property changes.
  useEffect(() => {
    if (!pickPropertyId) return;
    (async () => {
      const [sRes, schRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/properties/${pickPropertyId}/structure`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/ops/building-schedule/${pickPropertyId}`, { credentials: 'include' }),
      ]);
      const s = sRes.ok ? await sRes.json() : {};
      const sched = schRes.ok ? await schRes.json() : {};
      setStructure(s);
      const buildings = (s.buildings || []).map((b, i) => {
        const existing = (sched.buildings || []).find(
          (x) => String(x.building_number) === String(b.building_number),
        );
        return {
          building_number: b.building_number,
          units_count: (b.units || []).length,
          order: existing?.order ?? i + 1,
          assigned_agent_id: existing?.assigned_agent_id || '',
        };
      }).sort((a, b) => a.order - b.order).map((r, i) => ({ ...r, order: i + 1 }));
      setRows(buildings);
      setSelected({});
    })();
  }, [pickPropertyId]);

  const move = (idx, delta) => {
    setRows((prev) => {
      const next = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next.map((r, i) => ({ ...r, order: i + 1 }));
    });
  };

  const onDragStart = (idx) => setDragIndex(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => {
    if (dragIndex == null || dragIndex === idx) return;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      return next.map((r, i) => ({ ...r, order: i + 1 }));
    });
    setDragIndex(null);
  };

  const toggleSelect = (bn) => setSelected((s) => ({ ...s, [bn]: !s[bn] }));
  const selectAll = () => {
    const on = rows.every((r) => selected[r.building_number]);
    setSelected(on ? {} : Object.fromEntries(rows.map((r) => [r.building_number, true])));
  };
  const batchAssign = () => {
    if (!batchAgent) {
      toast.error('Pick a Site Agent to batch-assign first.');
      return;
    }
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) {
      toast.error('Select at least one building using the checkboxes.');
      return;
    }
    setRows((prev) => prev.map((r) => (
      selected[r.building_number] ? { ...r, assigned_agent_id: batchAgent } : r
    )));
    toast.success(`Assigned ${ids.length} building(s) to selected agent.`);
  };

  const setAssignFor = (bn, agentId) => {
    setRows((prev) => prev.map((r) => (
      r.building_number === bn ? { ...r, assigned_agent_id: agentId } : r
    )));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        buildings: rows.map((r) => ({
          building_number: String(r.building_number),
          order: r.order,
          assigned_agent_id: r.assigned_agent_id || null,
        })),
      };
      const res = await fetch(
        `${BACKEND_URL}/api/ops/building-schedule/${pickPropertyId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) throw new Error('save failed');
      toast.success('Building schedule saved.');
    } catch (e) {
      toast.error('Could not save the schedule. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const assignedCount = useMemo(
    () => rows.filter((r) => r.assigned_agent_id).length,
    [rows],
  );

  return (
    <Card
      className="bg-zinc-900 border-2 p-4 sm:p-6"
      style={{ borderColor: 'rgba(201, 169, 98, 0.5)' }}
      data-testid="building-scheduler-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <MapPin className="h-5 w-5 mr-2" style={{ color: '#C9A962' }} />
          BUILDING SCHEDULER · DRAG TO ORDER
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={pickPropertyId}
            onChange={(e) => setPickPropertyId(e.target.value)}
            className="h-9 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
            data-testid="scheduler-pick-property"
          >
            <option value="">Pick a community…</option>
            {(properties || []).map((p) => (
              <option key={p.property_id} value={p.property_id}>{p.property_name}</option>
            ))}
          </select>
        </div>
      </div>

      {!pickPropertyId && (
        <p className="text-xs text-zinc-400">Choose a community above to order its buildings and assign agents.</p>
      )}

      {pickPropertyId && rows.length === 0 && (
        <p className="text-xs text-zinc-400">
          No buildings on this community yet. Ask the Property Manager or NECS Admin to add the property structure.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Button
              size="sm"
              variant="outline"
              onClick={selectAll}
              className="border-zinc-600 text-zinc-200"
              data-testid="scheduler-select-all"
            >
              {rows.every((r) => selected[r.building_number]) ? 'Deselect all' : 'Select all'}
            </Button>
            <select
              value={batchAgent}
              onChange={(e) => setBatchAgent(e.target.value)}
              className="h-9 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-xs"
              data-testid="scheduler-batch-agent"
            >
              <option value="">Batch-assign agent…</option>
              {(agents || []).map((a) => (
                <option key={a.user_id} value={a.user_id}>{a.name}</option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={batchAssign}
              className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-semibold"
              data-testid="scheduler-batch-assign"
            >
              Batch assign
            </Button>
            <Badge className="bg-zinc-800 text-zinc-300 text-[10px]">
              {assignedCount}/{rows.length} buildings assigned
            </Badge>
          </div>

          <div className="space-y-2">
            {rows.map((r, idx) => (
              <div
                key={r.building_number}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(idx)}
                className={`flex items-center gap-2 p-2 rounded-md border ${dragIndex === idx ? 'border-[#FFD60A] bg-zinc-800' : 'border-zinc-700 bg-zinc-800/60'} cursor-grab`}
                data-testid={`scheduler-row-${r.building_number}`}
              >
                <input
                  type="checkbox"
                  checked={!!selected[r.building_number]}
                  onChange={() => toggleSelect(r.building_number)}
                  className="accent-[#FFD60A]"
                  data-testid={`scheduler-select-${r.building_number}`}
                />
                <GripVertical className="h-4 w-4 text-zinc-500" />
                <Badge className="bg-[#FFD60A] text-black text-[10px] font-bold w-8 justify-center">
                  {r.order}
                </Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">
                    Building {r.building_number}
                  </p>
                  <p className="text-[10px] text-zinc-400">{r.units_count} units</p>
                </div>
                <select
                  value={r.assigned_agent_id || ''}
                  onChange={(e) => setAssignFor(r.building_number, e.target.value)}
                  className="h-8 px-2 rounded bg-zinc-900 border border-zinc-700 text-white text-xs"
                  data-testid={`scheduler-agent-${r.building_number}`}
                >
                  <option value="">— Unassigned —</option>
                  {(agents || []).map((a) => (
                    <option key={a.user_id} value={a.user_id}>{a.name}</option>
                  ))}
                </select>
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    className="text-zinc-400 hover:text-white disabled:opacity-40"
                    disabled={idx === 0}
                    aria-label="Move up"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    className="text-zinc-400 hover:text-white disabled:opacity-40"
                    disabled={idx === rows.length - 1}
                    aria-label="Move down"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
              data-testid="scheduler-save"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : 'Save schedule'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export default BuildingScheduler;
